import {
  type api,
  type ResponseData,
  useInfiniteApiQuery,
  useTeam,
} from '@vrplatform/data';
import type { paidStatus_enum } from '@vrplatform/graphql';
import { day, type Maybe, sum } from '@vrplatform/utils';
import { SESSION_STICKY_VIEW_QUERY_OPTIONS } from '@vrplatform/data';
import { getExpenseLineCentTotal } from '../../_hooks';
import { useExpenseTableFilter } from './useExpenseTableFilter';

const PAGE_LIMIT = 50;
export const STORYBOOK_EXPENSE_TABLE_QUERY_KEY = 'expenses-table';

type ApiExpense = ResponseData<'get:/transactions'>['data'][number];

// ExpenseRow type - used by both API and GraphQL mappers
export type ExpenseRow = {
  id: string;
  status: 'active' | 'inactive';
  isOpeningBalance: boolean;
  date: string | null;
  // paidStatus: 'paid' | 'unpaid' | 'overpaid' | 'underpaid';
  // paidAt: string | null;
  payment: ApiExpense['payment'];
  recurringTemplateId: string | undefined;
  bankAccount:
    | {
        id: string;
        title: string;
      }
    | undefined;
  isReconciled: boolean;
  lock: Omit<NonNullable<ApiExpense['lock']>, 'status'> & {
    message: string | undefined;
    expense: 'locked' | 'unlocked';
    payment: 'locked' | 'unlocked';
  };
  issues: ApiExpense['issues'];
  attachedToOwnerStatementIds: string[];
  bankRecordIds: string[];
  attachments: ApiExpense['attachments'];
  contact: {
    id: string | undefined;
    name: string;
    type: string | undefined;
  };
  description: string;
  type: 'expense' | 'deposit' | 'transfer' | 'payout';
  uniqueRef: string | null | undefined;
  centTotal: number;
  currency: string;
  lines: {
    id: string;
    accountId: string | undefined;
    accountTitle: string | undefined;
    party: 'owners' | 'manager';
    centTotal: number;
    taxRateId: string | null;
    taxTitle: string | null;
    taxBasisPoints: number;
    description: string;
    markup: {
      centTotal: number;
      taxRateId: string | null;
      taxTitle: string | null;
      taxBasisPoints: number;
    } | null;
    listing: {
      id: string | undefined;
      name: string;
    };
    reservation: {
      id: string | undefined;
      confirmationCode: string | null | undefined;
      guestName: string | undefined;
      guests: number | undefined;
      checkIn: string | null | undefined;
      checkOut: string | null | undefined;
    };
  }[];
  connection:
    | {
        id: string;
        name: string;
        appId: string;
        logoUrl?: string | null;
      }
    | undefined;
};

/**
 * Maps API transaction response to ExpenseRow format
 */
export function mapExpenseRow(
  expense: ResponseData<'get:/transactions'>['data'][number]
): ExpenseRow {
  const lines = (expense.lines ?? []).map((line) => {
    const appliedTax = {
      taxRateId: line.appliedAmount?.taxRate?.id ?? null,
      taxTitle: line.appliedAmount?.taxRate?.name ?? null,
      taxBasisPoints: line.appliedAmount?.taxRate?.basisPoints ?? 0,
    };
    const markup = {
      centTotal: (line.markup?.amount ?? 0) * -1,
      taxRateId: line.markup?.taxRate?.id ?? null,
      taxTitle: line.markup?.taxRate?.name ?? null,
      taxBasisPoints: line.markup?.taxRate?.basisPoints ?? 0,
    };

    return {
      id: line.id,
      accountId: line.account?.id,
      accountTitle: line.account?.name,
      party: line.party ?? 'owners',
      centTotal: (line.amount ?? 0) * -1,
      taxRateId: appliedTax.taxRateId,
      taxTitle: appliedTax.taxTitle,
      taxBasisPoints: appliedTax.taxBasisPoints,
      description: line.description || '',
      markup: markup.centTotal ? markup : null,
      listing: {
        id: line.listing?.id,
        name: line.listing?.name || 'No name',
      },
      reservation: {
        id: line.reservation?.id,
        confirmationCode: line.reservation?.confirmationCode,
        guestName: undefined,
        guests: undefined,
        checkIn: line.reservation?.checkIn,
        checkOut: line.reservation?.checkOut,
      },
    };
  });

  const centTotal = sum(
    lines.map((line) => {
      const appliedTax = {
        rateId: line.taxRateId ?? null,
        basisPoints: line.taxBasisPoints ?? 0,
        title: line.taxTitle ?? '',
      };
      const markupTax = {
        rateId: line.markup?.taxRateId ?? null,
        basisPoints: line.markup?.taxBasisPoints ?? 0,
        title: line.markup?.taxTitle ?? '',
      };

      return getExpenseLineCentTotal({
        centTotal: line.centTotal ?? 0,
        markup: line.markup?.centTotal ?? 0,
        taxId: appliedTax.rateId,
        party: line.party,
        markupTaxId: markupTax.rateId,
        rates: [
          ...(appliedTax.rateId
            ? [
                {
                  label: appliedTax.title,
                  value: appliedTax.rateId,
                  percentageSuperBasePoints: appliedTax.basisPoints,
                },
              ]
            : []),
          ...(markupTax.rateId
            ? [
                {
                  label: markupTax.title,
                  value: markupTax.rateId,
                  percentageSuperBasePoints: markupTax.basisPoints,
                },
              ]
            : []),
        ],
      }).centTotal;
    })
  );

  const bankRecordIds = expense.payment.bankRecordIds ?? [];
  const isReconciled = bankRecordIds.length > 0;

  const attachedToOwnerStatementIds = expense.lock?.ownerStatement?.ids ?? [];

  const expenseLock = expense.lock?.status === 'locked' ? 'locked' : 'unlocked';
  const paymentLock =
    expense.payment.lock?.status === 'locked' ? 'locked' : 'unlocked';

  return {
    id: expense.id,
    status: expense.status || 'active',
    isOpeningBalance: expense.isOpeningBalance,
    date: expense.date,
    payment: expense.payment,
    issues: expense.issues,
    recurringTemplateId:
      expense.recurringTemplate?.id || expense.recurringTemplateId || undefined,
    bankAccount: expense.account?.id
      ? {
          id: expense.account.id,
          title: expense.account.name,
        }
      : undefined,
    isReconciled,
    lock: {
      ...expense.lock,
      expense: expenseLock,
      payment: paymentLock,
      message:
        expenseLock === 'locked' && paymentLock === 'locked'
          ? `This expense is locked and cannot be modified. ${expense.lock?.booksClosed ? `The books are closed through ${day(expense.lock.booksClosed.date).subtract(1, 'day').format('LL')}` : ''}`
          : expenseLock === 'locked' && paymentLock === 'unlocked'
            ? 'The expense is partially locked. You can still modify the payment status of the expense.'
            : expenseLock === 'unlocked' && paymentLock === 'locked'
              ? 'The expense is partially locked. You can modify everything except payment status of the expense.'
              : undefined,
    },
    attachedToOwnerStatementIds,
    bankRecordIds,
    attachments: expense.attachments ?? [],
    contact: {
      id: expense.contact?.id,
      name: expense.contact?.name ?? '-',
      type: undefined as string | undefined,
    },
    description: expense.description ?? '',
    type: expense.type ?? 'expense',
    uniqueRef: expense.uniqueRef,
    centTotal,
    currency: expense.currency,
    lines,
    connection: expense.connection?.id
      ? {
          id: expense.connection.id,
          name: expense.connection.name,
          appId: expense.connection.appId,
        }
      : undefined,
  };
}

type Filter = ReturnType<typeof useExpenseTableFilter>['filter'];

export const getExpenseTableQueryFilter = ({
  filter,
  limit = PAGE_LIMIT,
  page,
}: {
  filter: Filter;
  limit?: number;
  page: number;
}): NonNullable<api['/transactions']['get']['parameters']['query']> => {
  return {
    limit,
    page,
    search: filter.search?.trim() ?? undefined,
    accountId: filter.accountId ?? undefined,
    contactId: filter.contactId ?? undefined,
    paidStatus: (filter.paidStatus as Maybe<paidStatus_enum>) ?? undefined,
    reconciled: filter.reconciled ?? undefined,
    listingIds: filter.listingIds?.join(',') ?? undefined,
    recurringTemplateId: filter.recurring ?? undefined,
    connectionId: filter.connectionId
      ? filter.connectionId === 'manual'
        ? 'null'
        : filter.connectionId
      : undefined,
    type: 'expense',
    dateRange: filter.date ?? undefined,
    isDateRangeEndInclusive: filter.date?.includes('...') ? true : undefined,
    includeLines: true,
  };
};
export const useExpenseTableQuery = () => {
  const [{ id: teamId }] = useTeam();
  const { filter } = useExpenseTableFilter();

  return useInfiniteApiQuery(
    async ($api, signal, args, page, $db) => {
      const [response, hasSyncedExpense] = await Promise.all([
        $api.GET('/transactions', {
          params: {
            query: getExpenseTableQueryFilter({
              filter: args.filter,
              page,
            }),
          },
          signal,
        }),
        page === 1
          ? $db(
              (q) =>
                !!q
                  .transactions({
                    where: {
                      type: { _eq: 'expense' },
                      tenantId: { _eq: args.teamId },
                      connection: {
                        appId: { _neq: 'finalytic' },
                      },
                      connectionId: { _is_null: false },
                    },
                    limit: 1,
                  })
                  .map((tx) => tx.id)[0]
            )
          : false,
      ]);

      if (response.error) {
        throw response.error;
      }

      const list = response.data.data.map(mapExpenseRow);
      const connectionAppIds = Array.from(
        new Set(
          list
            .map((row) => row.connection?.appId)
            .filter(
              (appId): appId is string =>
                typeof appId === 'string' && appId.length > 0
            )
        )
      );
      const apps = await Promise.all(
        connectionAppIds.map(async (appId) => {
          const result = await $api.GET('/apps/{id}', {
            params: {
              path: {
                id: appId,
              },
            },
            signal,
          });

          if (result.error) {
            throw result.error;
          }

          return {
            id: appId,
            logoUrl: result.data.icon || null,
          };
        })
      );
      const appLogoById = new Map(
        apps.map((app) => [app.id, app.logoUrl || null] as const)
      );

      return {
        list: list.map((row) => ({
          ...row,
          connection: !row.connection
            ? row.connection
            : {
                ...row.connection,
                logoUrl: appLogoById.get(row.connection.appId) || null,
              },
        })),
        aggregate: response.data.pagination.total,
        hasSyncedExpense,
        pagination: response.data.pagination,
      };
    },
    {
      queryGroup: 'expenses',
      uniqueQueryKey: STORYBOOK_EXPENSE_TABLE_QUERY_KEY,
      storybookQueryKey: STORYBOOK_EXPENSE_TABLE_QUERY_KEY,
      variables: {
        teamId,
        filter,
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        return lastPage.pagination.nextPage ?? null;
      },
      hideNotification: true,
      ...SESSION_STICKY_VIEW_QUERY_OPTIONS,
    }
  );
};
